import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Copy, ExternalLink, Loader2, Mail, MessageCircle, Plus, Trash2, Upload, X } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  addCandidateNote, addProbationReview, candidateInterviewOutcome, day60Decision, emailCandidate, getCandidate,
  getCandidateLinks, getHiringMeta, hireCandidate, importHiringSheet, inviteCandidateInterview, listCandidates,
  makeCandidateOffer, rescoreCandidate, saveCandidateExam, saveHiringSettings, saveHiringSlots, saveScreeningCall,
  saveTrialResult, setCandidateInterview, setCandidateStage, setCandidateTrial, testHiringEmail,
} from "@/lib/api";

// Hiring, as the LIFTR Coach Hiring Playbook: application (scored by Claude against the one-pager) → 15-minute
// screening call → interview day exam (level) → paid trial week → offer → 60-day probation with weekly reviews
// → Day 60 (activate / hold at L1 / exit). Facility heads see their own gyms; admins see every gym.

const TABS = [["pipeline", "Pipeline"], ["interviews", "Interviews"], ["probation", "Probation"], ["pool", "Past applicants"],
  ["slots", "Interview slots"], ["setup", "Setup"]];
const BOARD = ["applied", "screening", "interview", "trial", "offer"];
const CLOSED = ["rejected", "withdrawn", "exited"];
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const STATUS_TONE = { "Strong PASS": "bg-emerald-500/15 text-emerald-300", PASS: "bg-emerald-500/10 text-emerald-400", HOLD: "bg-amber-500/15 text-amber-300", REJECT: "bg-rose-500/15 text-rose-300" };

const days = (iso) => {
  if (!iso) return "";
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  return d <= 0 ? "today" : d === 1 ? "1 day" : `${d} days`;
};
const ago = (iso) => (days(iso) === "today" ? "today" : `${days(iso)} ago`);
const when = (s) => (s ? new Date(s).toLocaleString("en-IN", { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }) : "");
const dayLabel = (s) => (s ? new Date(s).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" }) : "");
const wa = (phone, text) => `https://wa.me/${(phone || "").replace(/\D/g, "").replace(/^(\d{10})$/, "91$1")}?text=${encodeURIComponent(text)}`;
const copy = (t) => navigator.clipboard.writeText(t).then(() => toast.success("Copied"));
const err = (e, fallback) => toast.error(e?.response?.data?.detail || fallback);
const btn = "rounded-full border border-[#262626] px-3 py-1.5 text-xs text-zinc-200 hover:border-zinc-500 disabled:opacity-50";
const primary = "rounded-full bg-white text-black px-3 py-1.5 text-xs font-semibold hover:bg-zinc-200 disabled:opacity-50";
const fieldBase = "rounded-lg bg-[#111] border border-[#262626] px-3 py-2 text-sm text-white focus:border-zinc-500 focus:outline-none";
const field = `w-full ${fieldBase}`;
const pill = (on) => `rounded-full px-3 py-1 text-xs border ${on ? "bg-white text-black border-white" : "border-[#262626] text-zinc-300"}`;
const H = ({ children }) => <div className="text-xs uppercase tracking-[0.16em] text-zinc-500">{children}</div>;

function Chip({ c }) {
  if (c.app) return <span className={`rounded-full px-2 py-0.5 text-[11px] tabular-nums ${STATUS_TONE[c.app.status] || "bg-zinc-800 text-zinc-400"}`}>{c.app.status}{c.app.total != null ? ` ${c.app.total}/20` : ""}</span>;
  if (c.scoring_error) return <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] text-amber-300">Scoring failed</span>;
  return <span className="rounded-full bg-zinc-800 px-2 py-0.5 text-[11px] text-zinc-400">{c.screened ? "Scoring…" : "Not applied yet"}</span>;
}

function Card({ c, onOpen, gymName }) {
  const p = c.probation;
  return (
    <button type="button" onClick={() => onOpen(c.id)} data-testid={`candidate-${c.id}`}
      className="w-full text-left rounded-xl border border-[#1f1f1f] bg-[#0f0f0f] p-3 hover:border-zinc-600 space-y-1.5">
      <div className="flex items-start justify-between gap-2">
        <span className="font-medium text-zinc-100 min-w-0 break-words">{c.name}</span>
        <span className="shrink-0"><Chip c={c} /></span>
      </div>
      <div className="text-xs text-zinc-500 truncate">{(c.facility_id ? [c.facility_id] : c.gym_ids).map(gymName).join(", ") || "No gym"}{c.imported ? " · from sheet" : ""}</div>
      {c.call && <div className="text-xs text-zinc-400">Call {c.call.total}/35 · {c.call.decision}</div>}
      {c.exam && <div className="text-xs text-zinc-400">Interview {c.exam.total}/100{c.exam.level ? ` · ${c.exam.level}` : ""}</div>}
      {c.interview && c.stage === "interview" && <div className="text-xs text-sky-300">Interview day {when(c.interview)}</div>}
      {c.trial && c.stage === "trial" && <div className="text-xs text-sky-300">Trial {dayLabel(c.trial.start)} to {dayLabel(c.trial.end)}</div>}
      {p && c.stage === "probation" && (
        <div className="text-xs text-zinc-400">Day {p.day} of 60 · {p.reviews} reviews
          {p.review_due && <span className="text-amber-300"> · review due</span>}{p.decision_due && <span className="text-rose-300"> · Day 60 due</span>}
          {p.red_flags > 0 && <span className="text-rose-300"> · {p.red_flags} red flag{p.red_flags > 1 ? "s" : ""}</span>}</div>
      )}
      {c.app?.gates?.length > 0 && <div className="text-xs text-rose-300 truncate">{c.app.gates.join(" · ")}</div>}
      <div className="text-[11px] text-zinc-600">{days(c.stage_at) === "today" ? "New in stage today" : `${days(c.stage_at)} in stage`}</div>
    </button>
  );
}

function Pipeline({ meta, rows, onOpen, gymName }) {
  const [showClosed, setShowClosed] = useState(false);
  const [gym, setGym] = useState("");
  const list = rows.filter((c) => !gym || c.gym_ids.includes(gym) || c.facility_id === gym);
  const by = (s) => list.filter((c) => c.stage === s);
  const hint = { applied: "HOLD (10–12/20) or still being scored.", screening: "PASS 13+/20. Call within 2 working days.",
    interview: "Booking link sent. Theory + practical, 2 hours.", trial: "Paid, 5–7 days. Unsure = reject.", offer: "Waiting on their answer." };
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 items-center">
        <select value={gym} onChange={(e) => setGym(e.target.value)} className={`${fieldBase} w-auto`} data-testid="hiring-filter-gym">
          <option value="">All gyms</option>
          {meta.gyms.map((g) => <option key={g.id} value={g.id}>{g.city} · {g.name}</option>)}
        </select>
        <button type="button" className={btn} onClick={() => setShowClosed((x) => !x)} data-testid="hiring-show-closed">
          {showClosed ? "Hide closed" : `Closed (${list.filter((c) => CLOSED.includes(c.stage)).length})`}
        </button>
      </div>
      <div className="grid gap-3 overflow-x-auto pb-2" style={{ gridTemplateColumns: `repeat(${BOARD.length}, minmax(220px, 1fr))` }}>
        {BOARD.map((s) => (
          <div key={s} className="space-y-2" data-testid={`hiring-col-${s}`}>
            <div className="flex items-center justify-between px-1">
              <span className="text-xs uppercase tracking-[0.16em] text-zinc-400">{meta.stageLabel[s]}</span>
              <span className="text-xs text-zinc-600 tabular-nums">{by(s).length}</span>
            </div>
            <p className="px-1 text-[11px] text-zinc-600">{hint[s]}</p>
            {by(s).map((c) => <Card key={c.id} c={c} onOpen={onOpen} gymName={gymName} />)}
          </div>
        ))}
      </div>
      {showClosed && CLOSED.map((s) => by(s).length > 0 && (
        <div key={s} className="space-y-2">
          <H>{meta.stageLabel[s]} · {by(s).length}</H>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">{by(s).map((c) => <Card key={c.id} c={c} onOpen={onOpen} gymName={gymName} />)}</div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- the steps

function Application({ c, meta }) {
  const a = c.application;
  return (
    <div className="space-y-2">
      <H>Application{a ? ` · ${a.status} ${a.total ?? "–"}/20` : ""}{a?.imported ? " (scored in the sheet)" : ""}</H>
      {a?.gates?.length > 0 && <div className="text-rose-300">Hard gate: {a.gates.join(" · ")}</div>}
      {a?.ai_written && <div className="text-amber-300 flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5" />Reads like ChatGPT. Dig into it on the call.</div>}
      {a?.summary && <p className="text-zinc-400">{a.summary}</p>}
      {meta.written.map((q, i) => (c.answers?.[q.key] || a?.scores?.[q.key] != null) && (
        <div key={q.key} className="rounded-lg bg-[#0f0f0f] p-2.5">
          <div className="flex justify-between gap-2 text-xs text-zinc-500"><span>Q{q.key.slice(1)}. {q.label}</span>
            {a?.scores?.[q.key] != null && <span className={a.scores[q.key] >= 4 ? "text-emerald-300" : a.scores[q.key] <= 2 ? "text-rose-300" : "text-amber-300"}>{a.scores[q.key]}/5</span>}</div>
          {c.answers?.[q.key] && <p className="mt-1 whitespace-pre-wrap text-zinc-200">{c.answers[q.key]}</p>}
          {a?.reasons?.[q.key] && <p className="mt-1 text-xs text-zinc-500">{a.reasons[q.key]}</p>}
        </div>
      ))}
      {c.screened_at && !c.imported && (
        <div className="rounded-lg bg-[#0f0f0f] p-2.5 text-xs">
          <div className="text-zinc-500">Q4. Commitments ticked: {c.commitments?.length || 0} of 4</div>
          {meta.commitments.filter((x) => !(c.commitments || []).includes(x)).map((x) => <div key={x} className="text-rose-300">Not ticked: {x}</div>)}
        </div>
      )}
      {meta.info.filter((q) => c.answers?.[q.key] != null && c.answers[q.key] !== "" && !(Array.isArray(c.answers[q.key]) && !c.answers[q.key].length)).map((q) => (
        <div key={q.key} className="flex gap-2 text-xs"><span className="w-40 shrink-0 text-zinc-500">{q.label}</span>
          <span className="text-zinc-200">{q.key === "link" ? <a href={c.answers.link} target="_blank" rel="noreferrer" className="underline">{c.answers.link} <ExternalLink className="inline h-3 w-3" /></a>
            : Array.isArray(c.answers[q.key]) ? c.answers[q.key].join(", ") : String(c.answers[q.key])}</span></div>
      ))}
      {c.dob && <div className="flex gap-2 text-xs"><span className="w-40 shrink-0 text-zinc-500">Date of birth</span><span className="text-zinc-200">{c.dob}</span></div>}
    </div>
  );
}

function ScreeningCall({ c, meta, onSaved }) {
  const prev = c.screening_call;
  const [open, setOpen] = useState(!prev && c.stage === "screening");
  const [scores, setScores] = useState(prev?.scores || meta.screen.questions.map(() => 0));
  const [fail, setFail] = useState(!!prev?.auto_fail);
  const [decision, setDecision] = useState(prev?.decision || "");
  const [checks, setChecks] = useState(prev?.checks || {});
  const [notes, setNotes] = useState(prev?.notes || "");
  const total = scores.reduce((s, x) => s + x, 0);
  if (!open) {
    return (
      <div className="space-y-1">
        <H>Screening call{prev ? ` · ${prev.decision} ${prev.total}/35` : ""}</H>
        {prev ? <div className="text-xs text-zinc-500">{prev.by} · {ago(prev.at)}{prev.auto_fail ? " · auto-fail" : ""}</div> : <p className="text-zinc-500 text-xs">Not done yet.</p>}
        {prev?.notes && <p className="whitespace-pre-wrap text-zinc-300">{prev.notes}</p>}
        {Object.entries(prev?.checks || {}).map(([k, v]) => v && <div key={k} className="text-xs"><span className="text-zinc-500">{k}: </span>{v}</div>)}
        {["applied", "screening", "pool"].includes(c.stage) && <button type="button" className={btn} onClick={() => setOpen(true)}>{prev ? "Edit" : "Do the screening call"}</button>}
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-[#1f1f1f] p-3 space-y-3" data-testid="screening-call">
      <H>Screening call · 15 minutes · filter, not a sale</H>
      <blockquote className="border-l-2 border-amber-500/60 pl-3 text-zinc-300 italic">{meta.screen.opening}</blockquote>
      {meta.screen.questions.map((q, i) => (
        <div key={q.q} className="space-y-1">
          <div className="flex items-start justify-between gap-2">
            <span className="text-zinc-200">{i + 1}. {q.q}</span>
            <span className="flex gap-1 shrink-0">{[1, 2, 3, 4, 5].map((n) => (
              <button key={n} type="button" onClick={() => setScores((s) => s.map((x, j) => (j === i ? n : x)))}
                className={`h-7 w-7 rounded-md text-xs border ${scores[i] === n ? "bg-white text-black border-white" : "border-[#262626] text-zinc-400"}`}>{n}</button>))}</span>
          </div>
          <div className="text-[11px] text-zinc-500">Listen for: {q.listen} · <span className="text-rose-300/80">Red flag: {q.red}</span></div>
        </div>
      ))}
      <div className="grid gap-2 sm:grid-cols-2">
        {meta.screen.checks.map((k) => <input key={k} className={field} placeholder={k} value={checks[k] || ""} onChange={(e) => setChecks({ ...checks, [k]: e.target.value })} />)}
      </div>
      <label className="flex items-center gap-2 text-rose-300"><input type="checkbox" checked={fail} onChange={(e) => { setFail(e.target.checked); if (e.target.checked) setDecision("REJECT"); }} />
        An answer showed ego, entitlement or resistance to probation (auto-fail)</label>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-zinc-400 mr-1">Total {total}/35 ·</span>
        {["Strong PASS", "PASS", "HOLD", "REJECT"].map((d) => <button key={d} type="button" disabled={fail && d !== "REJECT"} onClick={() => setDecision(d)} className={pill(decision === d)}>{d}</button>)}
      </div>
      <p className="text-[11px] text-zinc-500">Final check: would you trust this person with 50 of our best clients?</p>
      <textarea rows={3} className={field} placeholder="What you heard" value={notes} onChange={(e) => setNotes(e.target.value)} />
      <div className="flex gap-2">
        <button type="button" className={primary} disabled={scores.some((s) => !s) || !decision}
          onClick={() => saveScreeningCall(c.id, { scores, auto_fail: fail, decision, checks, notes })
            .then((r) => { toast.success(decision === "REJECT" ? "Rejected, regret email sent" : "Saved"); setOpen(false); onSaved(r); }).catch((e) => err(e, "Couldn't save"))}>Save call</button>
        {prev && <button type="button" className={btn} onClick={() => setOpen(false)}>Cancel</button>}
      </div>
    </div>
  );
}

function Exam({ c, meta, onSaved }) {
  const prev = c.exam;
  const flat = meta.exam.flatMap((s) => s.items);
  const [open, setOpen] = useState(false);
  const [marks, setMarks] = useState(prev?.marks || flat.map(() => ""));
  const [level, setLevel] = useState(prev?.level || "");
  const [rec, setRec] = useState(prev?.recommendation || "");
  const [notes, setNotes] = useState(prev?.notes || "");
  const total = Math.round(marks.reduce((s, x) => s + (parseFloat(x) || 0), 0));
  const byMarks = total < 55 ? null : total < 70 ? "L1" : total < 85 ? "L2" : "L3";
  if (!open) {
    return (
      <div className="space-y-1">
        <H>Interview day{prev ? ` · ${prev.total}/100 · ${prev.level || "Reject"}` : ""}</H>
        {prev ? <div className="text-xs text-zinc-500">{prev.by} · {ago(prev.at)} · {prev.recommendation}{prev.level_by_marks !== prev.level && prev.level ? ` · marks gave ${prev.level_by_marks}` : ""}</div>
          : <p className="text-zinc-500 text-xs">Theory paper (A–C) and practical (D–E), 100 marks. Below 55 reject, 55–69 L1, 70–84 L2, 85+ L3.</p>}
        {prev?.notes && <p className="whitespace-pre-wrap text-zinc-300">{prev.notes}</p>}
        {["interview", "trial"].includes(c.stage) && <button type="button" className={c.stage === "interview" && !prev ? primary : btn} onClick={() => setOpen(true)} data-testid="hiring-exam-btn">{prev ? "Edit marks" : "Enter interview marks"}</button>}
      </div>
    );
  }
  let i = -1;
  return (
    <div className="rounded-xl border border-[#1f1f1f] p-3 space-y-3" data-testid="exam-form">
      <H>Interview day marks · marking guide inline (don't share with candidates)</H>
      {meta.exam.map((s) => (
        <div key={s.section} className="space-y-2">
          <div className="text-sm font-medium text-zinc-200">{s.section} ({s.items.reduce((x, it) => x + it.max, 0)})</div>
          {s.items.map((it) => {
            i += 1;
            const k = i;
            return (
              <div key={it.q} className="flex items-start gap-3">
                <div className="flex-1"><div className="text-zinc-300">{it.q}</div><div className="text-[11px] text-zinc-500">{it.guide}</div></div>
                <div className="flex items-center gap-1 shrink-0">
                  <input type="number" min={0} max={it.max} step={0.5} value={marks[k]} onChange={(e) => setMarks((m) => m.map((x, j) => (j === k ? e.target.value : x)))}
                    className={`${fieldBase} w-16 text-right`} />
                  <span className="text-xs text-zinc-500 w-6">/{it.max}</span>
                </div>
              </div>
            );
          })}
        </div>
      ))}
      <div className="rounded-lg bg-[#0f0f0f] p-2.5 flex flex-wrap items-center gap-2">
        <span className="text-zinc-200">Total {total}/100 → {byMarks || "Reject"}</span>
        {byMarks && ["L1", "L2", "L3"].filter((l) => l <= byMarks).map((l) => <button key={l} type="button" onClick={() => setLevel(l)} className={pill((level || byMarks) === l)}>{l}</button>)}
      </div>
      <p className="text-[11px] text-zinc-500">When unsure between levels, give the lower one; probation decides growth.</p>
      <div className="flex gap-1.5">{["Proceed", "Hold", "Reject"].map((r) => <button key={r} type="button" onClick={() => setRec(r)} className={pill(rec === r)}>{r}</button>)}</div>
      <textarea rows={3} className={field} placeholder="Interviewer comments" value={notes} onChange={(e) => setNotes(e.target.value)} />
      <div className="flex gap-2">
        <button type="button" className={primary} disabled={marks.some((m) => m === "") || !rec}
          onClick={() => saveCandidateExam(c.id, { marks: marks.map((m) => parseFloat(m) || 0), level: byMarks ? (level || byMarks) : null, recommendation: rec, notes })
            .then((r) => { toast.success(r.exam.level ? `Saved: ${r.exam.level}` : "Rejected, regret email sent"); setOpen(false); onSaved(r); }).catch((e) => err(e, "Couldn't save"))}>Save marks</button>
        <button type="button" className={btn} onClick={() => setOpen(false)}>Cancel</button>
      </div>
    </div>
  );
}

function Probation({ c, meta, onSaved }) {
  const st = c.probation_status;
  const p = c.probation;
  const [fb, setFb] = useState("");
  const [flags, setFlags] = useState([]);
  const [level, setLevel] = useState(p.level_offered || "L1");
  const [note, setNote] = useState("");
  const focus = meta.probation_focus.find(([upTo]) => (st?.week || 1) <= upTo) || meta.probation_focus[meta.probation_focus.length - 1];
  const decide = (decision) => {
    const msg = { activate: `Activate ${c.name} at ${level}? Their FPA skills unlock for ${level}.`, hold: "Hold at L1 for remediation, and review again in 30 days?", exit: `Exit ${c.name}? Remove their FPA access in User Management afterwards.` }[decision];
    if (window.confirm(msg)) day60Decision(c.id, { decision, level, note }).then(() => { toast.success("Saved"); onSaved(); }).catch((e) => err(e, "Couldn't save"));
  };
  return (
    <div className="space-y-3">
      <H>Probation · day {st?.day} of 60 · started {dayLabel(p.start)} · Day 60 on {dayLabel(p.decision_on)}{p.held ? " · held at L1" : ""}</H>
      <p className="text-xs text-zinc-500">Everyone works at L1 standards: group sessions only. No PT/SPT, program ownership, Trainerize publishing or orientations. Offered level: {p.level_offered}.</p>
      {(p.reviews || []).slice().reverse().map((r) => (
        <div key={r.id} className="rounded-lg bg-[#0f0f0f] p-2.5">
          <div className="text-xs text-zinc-500">Week {r.week} · {r.by} · {ago(r.at)}</div>
          <p className="whitespace-pre-wrap text-zinc-200 mt-1">{r.feedback}</p>
          {r.red_flags?.length > 0 && <div className="text-xs text-rose-300 mt-1">Red flag: {r.red_flags.join(", ")}</div>}
        </div>
      ))}
      {c.stage === "probation" && (
        <div className="rounded-xl border border-[#1f1f1f] p-3 space-y-2">
          <div className="text-sm text-zinc-200">Week {st?.week} review: {focus[1]}</div>
          <div className="text-[11px] text-zinc-500">Check: {focus[2]}</div>
          <textarea rows={3} className={field} placeholder="This week's written feedback (mandatory)" value={fb} onChange={(e) => setFb(e.target.value)} />
          <div className="flex flex-wrap gap-1.5">{meta.red_flags.map((f) => <button key={f} type="button" onClick={() => setFlags(flags.includes(f) ? flags.filter((x) => x !== f) : [...flags, f])}
            className={`rounded-full px-3 py-1 text-xs border ${flags.includes(f) ? "bg-rose-500/20 border-rose-500 text-rose-200" : "border-[#262626] text-zinc-400"}`}>{f}</button>)}</div>
          {flags.length > 0 && <p className="text-[11px] text-rose-300">Red flags: escalate to the facility head and Anish today.</p>}
          <button type="button" className={primary} disabled={fb.trim().length < 20}
            onClick={() => addProbationReview(c.id, { feedback: fb, red_flags: flags }).then(() => { setFb(""); setFlags([]); toast.success("Review saved"); onSaved(); }).catch((e) => err(e, "Couldn't save"))}>Save review</button>
        </div>
      )}
      {c.stage === "probation" && (
        <div className={`rounded-xl border p-3 space-y-2 ${st?.decision_due ? "border-rose-500/40" : "border-[#1f1f1f]"}`}>
          <div className="text-sm text-zinc-200">Day 60 decision{st?.decision_due ? " (due)" : ` (on ${dayLabel(p.decision_on)})`}. No default extensions.</div>
          <div className="flex items-center gap-1.5"><span className="text-xs text-zinc-500">Activate at</span>
            {["L1", "L2", "L3"].filter((l) => l <= (p.level_offered || "L1")).map((l) => <button key={l} type="button" onClick={() => setLevel(l)} className={pill(level === l)}>{l}</button>)}</div>
          <input className={field} placeholder="Note (why)" value={note} onChange={(e) => setNote(e.target.value)} />
          <div className="flex flex-wrap gap-2">
            <button type="button" className={primary} onClick={() => decide("activate")}>Activate</button>
            <button type="button" className={btn} onClick={() => decide("hold")}>Hold at L1</button>
            <button type="button" className={btn} onClick={() => decide("exit")}>Exit</button>
          </div>
          <p className="text-[11px] text-zinc-500">Would you trust this coach with 50 of our best clients?</p>
        </div>
      )}
      {p.outcome && <div className="text-zinc-300">Outcome: {p.outcome.decision}{p.outcome.level ? ` at ${p.outcome.level}` : ""} · {p.outcome.by} · {ago(p.outcome.at)}</div>}
    </div>
  );
}

function Detail({ id, meta, gymName, onClose, reload }) {
  const [c, setC] = useState(null);
  const [links, setLinks] = useState(null);
  const [form, setForm] = useState(null);
  const [v, setV] = useState({});
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    getCandidate(id).then(setC).catch((e) => err(e, "Couldn't load"));
    getCandidateLinks(id).then(setLinks).catch(() => {});
  }, [id]);
  useEffect(() => { load(); setForm(null); setV({}); }, [load]);
  const refresh = () => { load(); reload(); };

  const act = async (fn, ok) => {
    setBusy(true);
    try {
      const r = await fn();
      if (r?.email && !r.email.sent) toast.warning(`${ok}. Email not sent: ${r.email.reason}`);
      else toast.success(ok);
      setForm(null); refresh();
    } catch (e) { err(e, "That didn't work"); }
    finally { setBusy(false); }
  };
  if (!c) return <div className="p-6"><Loader2 className="h-5 w-5 animate-spin text-zinc-500" /></div>;

  const first = c.name.split(" ")[0];
  const examLevel = c.exam?.level;
  const callPassed = ["PASS", "Strong PASS"].includes(c.screening_call?.decision);

  return (
    <div className="space-y-5 text-sm" data-testid="candidate-detail">
      <div className="space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="rounded-full border border-[#262626] px-2 py-0.5 text-xs">{meta.stageLabel[c.stage]}</span>
          {c.level && <span className="rounded-full border border-[#262626] px-2 py-0.5 text-xs">{meta.levels[c.level]?.label}</span>}
          {c.source?.utm_source && <span className="text-xs text-zinc-500">from {c.source.utm_source}{c.source.utm_campaign ? ` / ${c.source.utm_campaign}` : ""}</span>}
          {c.source?.from && <span className="text-xs text-zinc-500">{c.source.from}</span>}
        </div>
        <div className="text-zinc-400">{c.phone} · {c.email}{c.area ? ` · ${c.area}` : ""}</div>
        <div className="text-zinc-500 text-xs">Wants {c.gym_ids.map(gymName).join(", ") || "–"} · applied {ago(c.created_at)}</div>
        <div className="flex flex-wrap gap-2 pt-2">
          <a className={btn} href={`tel:${c.phone}`}>Call</a>
          <a className={btn} target="_blank" rel="noreferrer" href={wa(c.phone, `Hi ${first}, this is the LIFTR team about your coaching application.`)}><MessageCircle className="inline h-3 w-3 mr-1" />WhatsApp</a>
          {links && <button type="button" className={btn} onClick={() => copy(c.stage === "interview" ? links.book : links.apply)}>
            <Copy className="inline h-3 w-3 mr-1" />{c.stage === "interview" ? "Booking link" : "Application link"}</button>}
        </div>
      </div>

      {!["probation", "active", "exited"].includes(c.stage) && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 space-y-2">
          <H><span className="text-amber-300">Next step</span></H>
          {c.stage === "applied" && !c.screened_at && <p className="text-zinc-300">They came from the landing page and haven't filled in the application. The link was emailed; send it on WhatsApp too.</p>}
          {c.stage === "applied" && c.scoring_error && <button type="button" className={primary} disabled={busy} onClick={() => act(() => rescoreCandidate(c.id), "Scored")}>Score the application now</button>}
          <div className="flex flex-wrap gap-2">
            {(callPassed || c.stage === "pool") && ["applied", "screening", "pool"].includes(c.stage) && (
              <button type="button" className={primary} disabled={busy} data-testid="hiring-invite-interview"
                onClick={() => act(() => inviteCandidateInterview(c.id), "Invited to interview day")}>Invite to interview day</button>
            )}
            {["applied", "pool"].includes(c.stage) && <button type="button" className={btn} disabled={busy} onClick={() => act(() => setCandidateStage(c.id, { stage: "screening" }), "Moved to screening call")}>Move to screening call</button>}
            {["screening", "interview"].includes(c.stage) && <button type="button" className={btn} onClick={() => setForm(form === "interview" ? null : "interview")}>Set interview time myself</button>}
            {c.stage === "interview" && c.interview?.status === "booked" && !c.exam && (
              <button type="button" className={btn} disabled={busy} onClick={() => act(() => candidateInterviewOutcome(c.id, "no_show"), "Marked no-show")}>No-show</button>
            )}
            {examLevel && c.stage === "interview" && <button type="button" className={primary} onClick={() => setForm(form === "trial" ? null : "trial")} data-testid="hiring-trial-btn">Book paid trial week</button>}
            {c.stage === "trial" && !c.trial?.result && <button type="button" className={primary} onClick={() => setForm(form === "result" ? null : "result")}>Trial week result</button>}
            {(c.trial?.result?.decision === "hire" || (c.imported && c.stage === "trial")) && ["trial", "offer"].includes(c.stage) && (
              <button type="button" className={c.stage === "trial" ? primary : btn} onClick={() => setForm(form === "offer" ? null : "offer")} data-testid="hiring-offer-btn">{c.stage === "offer" ? "Change offer" : "Make offer"}</button>
            )}
            {c.stage === "offer" && (
              <button type="button" className={primary} disabled={busy} data-testid="hiring-hire"
                onClick={() => window.confirm(`${c.name} accepted? This gives them FPA access at ${gymName(c.offer?.facility_id)} (group sessions only) and starts their 60-day probation.`)
                  && act(() => hireCandidate(c.id), "Hired. Probation started")}>They accepted: start probation</button>
            )}
            {!["rejected", "withdrawn"].includes(c.stage) && (
              <>
                <button type="button" className={btn} disabled={busy} onClick={() => {
                  const reason = window.prompt("Why are we rejecting them? (staff only, not emailed)");
                  if (reason !== null) act(() => setCandidateStage(c.id, { stage: "rejected", reason, email: true }), "Rejected, regret email sent");
                }}>Reject</button>
                <button type="button" className={btn} disabled={busy} onClick={() => act(() => setCandidateStage(c.id, { stage: "withdrawn", email: false }), "Marked withdrawn")}>They withdrew</button>
                {c.stage !== "pool" && <button type="button" className={btn} disabled={busy} onClick={() => act(() => setCandidateStage(c.id, { stage: "pool", email: false }), "Moved to past applicants")}>Park in past applicants</button>}
              </>
            )}
            {["rejected", "withdrawn"].includes(c.stage) && <button type="button" className={btn} disabled={busy} onClick={() => act(() => setCandidateStage(c.id, { stage: "applied", email: false }), "Reopened")}>Reopen</button>}
          </div>

          {form === "interview" && (
            <div className="grid gap-2 sm:grid-cols-3 pt-2">
              <select className={field} value={v.ifid || ""} onChange={(e) => setV({ ...v, ifid: e.target.value })}>
                <option value="">Gym</option>{meta.gyms.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
              <input type="datetime-local" className={field} value={v.istart || ""} onChange={(e) => setV({ ...v, istart: e.target.value })} />
              <button type="button" className={primary} disabled={busy || !v.ifid || !v.istart}
                onClick={() => act(() => setCandidateInterview(c.id, { facility_id: v.ifid, start: v.istart }), "Interview day booked and emailed")}>Book and email</button>
            </div>
          )}
          {form === "trial" && (
            <div className="grid gap-2 sm:grid-cols-2 pt-2">
              <select className={field} value={v.tfid || c.facility_id || ""} onChange={(e) => setV({ ...v, tfid: e.target.value })}>
                <option value="">Gym</option>{meta.gyms.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
              <input className={field} placeholder="Pay, e.g. ₹1,000 a day" value={v.tpay || ""} onChange={(e) => setV({ ...v, tpay: e.target.value })} />
              <label className="text-xs text-zinc-500">From<input type="date" className={field} value={v.ts || ""} onChange={(e) => setV({ ...v, ts: e.target.value })} /></label>
              <label className="text-xs text-zinc-500">To (5–7 days)<input type="date" className={field} value={v.te || ""} onChange={(e) => setV({ ...v, te: e.target.value })} /></label>
              <button type="button" className={primary} disabled={busy || !(v.tfid || c.facility_id) || !v.ts || !v.te}
                onClick={() => act(() => setCandidateTrial(c.id, { facility_id: v.tfid || c.facility_id, start: v.ts, end: v.te, pay: v.tpay || "" }), "Trial week booked and emailed")}>Book and email</button>
            </div>
          )}
          {form === "result" && (
            <div className="space-y-2 pt-2">
              <div className="flex gap-1.5">{[["hire", "Hire"], ["reject", "Reject"]].map(([k, l]) => <button key={k} type="button" onClick={() => setV({ ...v, rd: k })} className={pill(v.rd === k)}>{l}</button>)}</div>
              <textarea rows={3} className={field} placeholder="What you saw this week (punctuality, energy, coaching, members' feedback)" value={v.rn || ""} onChange={(e) => setV({ ...v, rn: e.target.value })} />
              <p className="text-[11px] text-zinc-500">Uncertainty leads to rejection.</p>
              <button type="button" className={primary} disabled={busy || !v.rd || (v.rn || "").trim().length < 10}
                onClick={() => act(() => saveTrialResult(c.id, { decision: v.rd, notes: v.rn }), v.rd === "hire" ? "Saved. Make the offer next" : "Rejected, regret email sent")}>Save</button>
            </div>
          )}
          {form === "offer" && (
            <div className="grid gap-2 sm:grid-cols-2 pt-2">
              <select className={field} value={v.ofid || c.facility_id || ""} onChange={(e) => setV({ ...v, ofid: e.target.value })}>
                <option value="">Gym</option>{meta.gyms.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
              <div className="flex items-center gap-1.5">{["L1", "L2", "L3"].filter((l) => !examLevel || l <= examLevel).map((l) => (
                <button key={l} type="button" onClick={() => setV({ ...v, olevel: l })} className={pill((v.olevel || examLevel || "L1") === l)}>{l}</button>))}</div>
              <input className={field} placeholder="Monthly salary, e.g. ₹30,000" value={v.osalary || ""} onChange={(e) => setV({ ...v, osalary: e.target.value })} />
              <label className="text-xs text-zinc-500">Start date<input type="date" className={field} value={v.ostart || ""} onChange={(e) => setV({ ...v, ostart: e.target.value })} /></label>
              <textarea rows={2} className={`${field} sm:col-span-2`} placeholder="Anything else for the offer email (optional)" value={v.onote || ""} onChange={(e) => setV({ ...v, onote: e.target.value })} />
              <button type="button" className={primary} disabled={busy || !(v.ofid || c.facility_id) || !v.ostart || !v.osalary}
                onClick={() => act(() => makeCandidateOffer(c.id, { facility_id: v.ofid || c.facility_id, level: v.olevel || examLevel || "L1", salary: v.osalary, start_date: v.ostart, note: v.onote || "" }), "Offer emailed")}>Send offer</button>
            </div>
          )}
        </div>
      )}

      {c.interview?.start && <div className="text-zinc-300">Interview day: <b>{when(c.interview.start)}</b> at {gymName(c.facility_id)} · {c.interview.status}{c.interview.host ? ` · host ${c.interview.host}` : ""}</div>}
      {c.trial && <div className="text-zinc-300">Trial week: <b>{dayLabel(c.trial.start)} to {dayLabel(c.trial.end)}</b> at {gymName(c.trial.facility_id)}{c.trial.pay ? ` · ${c.trial.pay}` : ""}{c.trial.result ? ` · ${c.trial.result.decision}: ${c.trial.result.notes}` : ""}</div>}
      {c.offer && <div className="text-zinc-300">Offer: <b>{c.offer.level}</b>, {c.offer.salary}, from {dayLabel(c.offer.start_date)} at {gymName(c.offer.facility_id)}</div>}

      {c.probation && <Probation c={c} meta={meta} onSaved={refresh} />}
      {(c.exam || ["interview", "trial"].includes(c.stage)) && <Exam key={`exam-${c.exam?.at || ""}`} c={c} meta={meta} onSaved={refresh} />}
      <ScreeningCall key={`call-${c.screening_call?.at || ""}`} c={c} meta={meta} onSaved={refresh} />
      <Application c={c} meta={meta} />

      <div className="space-y-2">
        <H>Notes</H>
        {(c.notes || []).map((n) => <div key={n.id} className="rounded-lg bg-[#0f0f0f] p-2.5"><div className="whitespace-pre-wrap">{n.text}</div><div className="text-xs text-zinc-600 mt-1">{n.by} · {ago(n.at)}</div></div>)}
        <div className="flex gap-2">
          <input className={field} placeholder="Add a note" value={note} onChange={(e) => setNote(e.target.value)} />
          <button type="button" className={btn} disabled={!note.trim()} onClick={() => addCandidateNote(c.id, note).then(() => { setNote(""); load(); })}>Add</button>
        </div>
      </div>

      <div className="space-y-2">
        <H>Emails</H>
        {(c.emails || []).slice().reverse().map((m, i) => (
          <div key={i} className="flex items-start gap-2 text-xs">
            <Mail className={`h-3.5 w-3.5 mt-0.5 ${m.sent ? "text-emerald-400" : "text-amber-400"}`} />
            <div><span className="text-zinc-300">{m.subject}</span> <span className="text-zinc-600">· {ago(m.at)}{m.sent ? "" : ` · not sent: ${m.reason}`}</span></div>
          </div>
        ))}
        <div className="flex gap-2 items-center">
          <select className={`${fieldBase} w-auto`} value={v.tpl || ""} onChange={(e) => setV({ ...v, tpl: e.target.value })}>
            <option value="">Send an email again…</option>
            {Object.entries(meta.templates).map(([k, s]) => <option key={k} value={k}>{s.replace("{first}", first).replace("{when}", "…")}</option>)}
          </select>
          <button type="button" className={btn} disabled={!v.tpl || busy} onClick={() => act(() => emailCandidate(c.id, v.tpl).then((m) => ({ email: m })), "Email sent")}>Send</button>
        </div>
        <details className="text-xs text-zinc-500">
          <summary className="cursor-pointer">History</summary>
          <ul className="mt-2 space-y-1">
            {(c.events || []).slice().reverse().map((e, i) => (
              <li key={i}>{new Date(e.at).toLocaleString("en-IN")} · {e.by} · {e.kind}{e.to ? `: ${meta.stageLabel[e.frm] || e.frm} → ${meta.stageLabel[e.to]}` : ""}{e.reason ? ` (${e.reason})` : ""}{e.start ? ` ${when(e.start)}` : ""}{e.status ? ` ${e.status}` : ""}</li>
            ))}
          </ul>
        </details>
      </div>
      <button type="button" className={btn} onClick={onClose}>Close</button>
    </div>
  );
}

// ---------------------------------------------------------------- other tabs

function Row({ c, at, onOpen, gymName }) {
  return (
    <button type="button" onClick={() => onOpen(c.id)} className="w-full text-left flex items-center justify-between gap-3 rounded-lg border border-[#1f1f1f] px-3 py-2 hover:border-zinc-600">
      <span><span className="text-zinc-100">{c.name}</span> <span className="text-xs text-zinc-500">· {gymName(c.facility_id) || c.gym_ids.map(gymName).join(", ")}</span></span>
      <span className="text-xs text-zinc-300 text-right">{at}</span>
    </button>
  );
}

function Interviews({ rows, onOpen, gymName }) {
  const up = rows.filter((c) => c.interview && c.stage === "interview" && !c.exam).sort((a, b) => a.interview.localeCompare(b.interview));
  const waiting = rows.filter((c) => c.stage === "interview" && !c.interview && !c.exam);
  const trials = rows.filter((c) => c.stage === "trial" && c.trial).sort((a, b) => a.trial.start.localeCompare(b.trial.start));
  const Col = ({ title, list, at, empty }) => (
    <section className="space-y-2"><H>{title}</H>{list.length ? list.map((c) => <Row key={c.id} c={c} at={at(c)} onOpen={onOpen} gymName={gymName} />) : <p className="text-zinc-600 text-sm">{empty}</p>}</section>
  );
  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <Col title="Interview days booked" list={up} at={(c) => when(c.interview)} empty="None booked." />
      <Col title="Invited, not booked yet" list={waiting} at={(c) => `invited ${ago(c.stage_at)}`} empty="Nobody waiting." />
      <Col title="Trial weeks" list={trials} at={(c) => `${dayLabel(c.trial.start)} to ${dayLabel(c.trial.end)}`} empty="None booked." />
    </div>
  );
}

function ProbationTab({ rows, onOpen, gymName }) {
  const on = rows.filter((c) => c.stage === "probation").sort((a, b) => (b.probation?.day || 0) - (a.probation?.day || 0));
  const active = rows.filter((c) => c.stage === "active");
  const exited = rows.filter((c) => c.stage === "exited");
  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <section className="space-y-2"><H>On probation · {on.length}</H>
        {on.length ? on.map((c) => <Row key={c.id} c={c} onOpen={onOpen} gymName={gymName}
          at={<>Day {c.probation.day}/60 · {c.probation.reviews} reviews{c.probation.review_due && <span className="text-amber-300"> · review due</span>}{c.probation.decision_due && <span className="text-rose-300"> · Day 60 due</span>}{c.probation.red_flags > 0 && <span className="text-rose-300"> · red flags</span>}</>} />)
          : <p className="text-zinc-600 text-sm">Nobody on probation.</p>}</section>
      <section className="space-y-2"><H>Activated · {active.length}</H>
        {active.map((c) => <Row key={c.id} c={c} at={c.level} onOpen={onOpen} gymName={gymName} />)}</section>
      <section className="space-y-2"><H>Exited · {exited.length}</H>
        {exited.map((c) => <Row key={c.id} c={c} at={ago(c.stage_at)} onOpen={onOpen} gymName={gymName} />)}</section>
    </div>
  );
}

function Pool({ rows, onOpen, gymName }) {
  const [q, setQ] = useState("");
  const list = rows.filter((c) => c.stage === "pool" && (!q || `${c.name} ${c.email} ${c.phone}`.toLowerCase().includes(q.toLowerCase())));
  return (
    <div className="space-y-3 max-w-3xl">
      <p className="text-sm text-zinc-400">People who passed the application before but never finished the process (mostly from the old Hiring sheet). They're kept so nobody applies twice unnoticed. Open one to call them again or invite them straight to interview day.</p>
      <input className={`${fieldBase} w-72`} placeholder="Search name, email, phone" value={q} onChange={(e) => setQ(e.target.value)} />
      <div className="space-y-1.5">{list.slice(0, 200).map((c) => <Row key={c.id} c={c} onOpen={onOpen} gymName={gymName}
        at={<>{c.app?.status}{c.app?.total != null ? ` ${c.app.total}/20` : ""}{c.call ? ` · call ${c.call.decision}` : ""}</>} />)}</div>
      {list.length > 200 && <p className="text-xs text-zinc-600">Showing 200 of {list.length}. Search to narrow it down.</p>}
    </div>
  );
}

function Slots({ meta, reload }) {
  const [slots, setSlots] = useState(meta.slots);
  const [hosts, setHosts] = useState(meta.hosts);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await saveHiringSlots({ slots, hosts }); toast.success("Saved"); reload(); }
    catch (e) { err(e, "Couldn't save"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-5 max-w-3xl">
      <p className="text-sm text-zinc-400">Weekly windows when each gym can run interview days (gym time). Candidates book 2-hour slots from the invite email, at least 18 hours ahead and up to two weeks out. Pick hours when the interviewer is free for the practical and there's space on the floor.</p>
      {meta.gyms.map((g) => (
        <div key={g.id} className="rounded-xl border border-[#1f1f1f] p-4 space-y-3" data-testid={`slots-${g.id}`}>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <span className="font-medium">{g.city} · {g.name}</span>
            <input className={`${fieldBase} w-56`} placeholder="Ask for (interviewer)" value={hosts[g.id] || ""} onChange={(e) => setHosts({ ...hosts, [g.id]: e.target.value })} />
          </div>
          {slots.map((w, i) => w.facility_id === g.id && (
            <div key={i} className="flex gap-2 items-center">
              <select className={`${fieldBase} w-24`} value={w.weekday} onChange={(e) => setSlots(slots.map((x, j) => (j === i ? { ...x, weekday: Number(e.target.value) } : x)))}>
                {DAYS.map((d, n) => <option key={d} value={n}>{d}</option>)}
              </select>
              <input type="time" className={`${fieldBase} w-32`} value={w.start} onChange={(e) => setSlots(slots.map((x, j) => (j === i ? { ...x, start: e.target.value } : x)))} />
              <span className="text-zinc-500">to</span>
              <input type="time" className={`${fieldBase} w-32`} value={w.end} onChange={(e) => setSlots(slots.map((x, j) => (j === i ? { ...x, end: e.target.value } : x)))} />
              <button type="button" aria-label="Remove" className="text-zinc-500 hover:text-rose-300" onClick={() => setSlots(slots.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          <button type="button" className={btn} onClick={() => setSlots([...slots, { facility_id: g.id, weekday: 1, start: "10:00", end: "12:00" }])}><Plus className="inline h-3 w-3 mr-1" />Add a window</button>
        </div>
      ))}
      <button type="button" className={primary} disabled={busy} onClick={save} data-testid="slots-save">Save interview slots</button>
    </div>
  );
}

function ImportSheet({ reload }) {
  const [file, setFile] = useState(null);
  const [dry, setDry] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async (apply) => {
    setBusy(true);
    try {
      const r = await importHiringSheet(file, apply);
      setDry(r);
      if (apply) { toast.success(`Imported ${r.new}`); reload(); }
    } catch (e) { err(e, "Couldn't read that file"); }
    finally { setBusy(false); }
  };
  return (
    <section className="space-y-2">
      <H>Import from the old Hiring sheet</H>
      <p className="text-zinc-400">In Google Sheets, open a screening tab (BLR, HYD, Sri Lanka, Dubai) → File → Download → CSV, then upload it here. Only open candidates come in: application PASS or HOLD, screening not rejected, no interview result. Anyone scored in the last 45 days goes back on the board; older ones go to Past applicants. People already in FPA are skipped.</p>
      <div className="flex flex-wrap items-center gap-2">
        <label className={`${btn} cursor-pointer`}><Upload className="inline h-3 w-3 mr-1" />{file ? file.name : "Choose CSV"}
          <input type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => { setFile(e.target.files?.[0] || null); setDry(null); }} /></label>
        <button type="button" className={btn} disabled={!file || busy} onClick={() => run(false)}>Check</button>
        {dry && !dry.applied && dry.new > 0 && <button type="button" className={primary} disabled={busy} onClick={() => run(true)}>Import {dry.new}</button>}
      </div>
      {dry && (
        <div className="text-xs text-zinc-400">
          {dry.applied ? "Imported" : "Would import"} {dry.new} of {dry.found} open rows: {Object.entries(dry.by_stage).map(([k, n]) => `${n} ${k}`).join(", ") || "none new"}.
          {dry.sample?.length > 0 && <div className="mt-1 text-zinc-500">{dry.sample.map((s) => `${s.name} (${s.status} → ${s.stage})`).join(" · ")}</div>}
        </div>
      )}
    </section>
  );
}

function Setup({ meta, reload }) {
  const s = meta.settings;
  const [loc, setLoc] = useState(s.ghl_location_id);
  const [token, setToken] = useState("");
  const [from, setFrom] = useState(s.from_email);
  const [busy, setBusy] = useState(false);
  const [forms, setForms] = useState((s.meta_forms || []).map((f) => `${f.id}, ${f.city}`).join("\n"));
  const metaForms = () => forms.split("\n").map((l) => l.split(",").map((x) => x.trim())).filter(([id]) => id).map(([id, city]) => ({ id, city: city || "" }));
  const save = async (extra = {}) => {
    setBusy(true);
    try { await saveHiringSettings({ ghl_location_id: loc, token: token || null, from_email: from, ...extra }); setToken(""); toast.success("Saved"); reload(); }
    catch (e) { err(e, "Couldn't save"); }
    finally { setBusy(false); }
  };
  const utm = (src) => `${meta.apply_link}?utm_source=${src}&utm_campaign=coach-hiring`;
  return (
    <div className="space-y-6 max-w-3xl text-sm">
      <section className="space-y-2">
        <H>Application links</H>
        <p className="text-zinc-400">One link per channel, so the pipeline shows where hires come from. Point the landing page's Apply button at the first one.</p>
        {[["Landing page", "landing"], ["Meta ads", "meta"], ["Instagram bio / stories", "instagram"], ["WhatsApp", "whatsapp"], ["Job sites", "jobsite"], ["Staff referral", "referral"]].map(([label, src]) => (
          <div key={src} className="flex items-center gap-2">
            <span className="w-44 shrink-0 text-zinc-300">{label}</span>
            <code className="flex-1 truncate rounded bg-[#111] px-2 py-1 text-xs text-zinc-400">{utm(src)}</code>
            <button type="button" className={btn} onClick={() => copy(utm(src))}><Copy className="h-3 w-3" /></button>
          </div>
        ))}
      </section>
      {meta.is_admin ? (
        <>
          <ImportSheet reload={reload} />
          <section className="space-y-2">
            <H>Emails (GoHighLevel: Bangalore Master)</H>
            <p className="text-zinc-400">Candidate emails go out through this account, not a gym's, so applicants stay out of the member CRM. Use a Private Integration token with contacts and conversations scopes.</p>
            <div className={`text-xs ${meta.email_ready ? "text-emerald-300" : "text-amber-300"}`}>{meta.email_ready ? "Connected" : "Not connected: emails are logged on each candidate but not sent"}</div>
            <input className={field} placeholder="Location ID" value={loc} onChange={(e) => setLoc(e.target.value)} />
            <input className={field} placeholder={s.token_set ? "Token saved (paste a new one to replace it)" : "Private Integration token"} value={token} onChange={(e) => setToken(e.target.value)} type="password" autoComplete="off" />
            <input className={field} placeholder="From" value={from} onChange={(e) => setFrom(e.target.value)} />
            <div className="flex gap-2">
              <button type="button" className={primary} disabled={busy} onClick={() => save()}>Save</button>
              <button type="button" className={btn} disabled={busy || !meta.email_ready} onClick={() => testHiringEmail().then((r) => (r.sent ? toast.success(`Test sent to ${r.to}`) : toast.error(r.reason)))}>Send me a test</button>
            </div>
          </section>
          <section className="space-y-2">
            <H>Meta instant forms → FPA</H>
            <p className="text-zinc-400">Hiring ads with an instant form: FPA pulls new leads every 5 minutes, prefills their application, emails them the finish link and DMs the facility head on Discord. One form per line: form ID, city (Bangalore, Hyderabad, Colombo or Dubai).</p>
            <div className={`text-xs ${s.meta_ready ? "text-emerald-300" : "text-amber-300"}`}>{s.meta_ready ? "Meta connected" : "META_ACCESS_TOKEN isn't set on the server, so no leads are pulled yet"}</div>
            <textarea className={field} rows={3} placeholder="1657241632721318, Colombo" value={forms} onChange={(e) => setForms(e.target.value)} />
            <button type="button" className={primary} disabled={busy} onClick={() => save({ meta_forms: metaForms() })}>Save forms</button>
          </section>
          <section className="space-y-2">
            <H>Landing page form → FPA</H>
            <p className="text-zinc-400">If the landing page keeps its own form: in Bangalore Master, add a workflow "Form submitted → Webhook (POST)" to this URL. FPA creates the candidate and emails them the application.</p>
            {s.webhook ? (
              <div className="flex items-center gap-2">
                <code className="flex-1 truncate rounded bg-[#111] px-2 py-1 text-xs text-zinc-400">{s.webhook}</code>
                <button type="button" className={btn} onClick={() => copy(s.webhook)}><Copy className="h-3 w-3" /></button>
                <button type="button" className={btn} disabled={busy} onClick={() => window.confirm("Make a new key? The old webhook URL stops working.") && save({ new_webhook_key: true })}>New key</button>
              </div>
            ) : <button type="button" className={btn} disabled={busy} onClick={() => save()}>Create webhook URL</button>}
          </section>
        </>
      ) : <p className="text-zinc-500">An admin connects email, the landing page webhook and the sheet import.</p>}
    </div>
  );
}

export default function Hiring() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "pipeline";
  const open = params.get("c");
  const [meta, setMeta] = useState(null);
  const [rows, setRows] = useState([]);
  const load = useCallback(() => {
    getHiringMeta().then((m) => setMeta({ ...m, stageLabel: Object.fromEntries(m.stages.map((s) => [s.key, s.label])) })).catch((e) => err(e, "Couldn't load hiring"));
    listCandidates().then((r) => setRows(r.candidates)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const gymName = useMemo(() => {
    const m = Object.fromEntries((meta?.gyms || []).map((g) => [g.id, g.name]));
    return (id) => m[id] || "";
  }, [meta]);
  const setQ = (k, v) => { const p = new URLSearchParams(params); v ? p.set(k, v) : p.delete(k); setParams(p, { replace: true }); };
  if (!meta) return <div className="p-6 flex items-center gap-2 text-zinc-500"><Loader2 className="h-4 w-4 animate-spin" />Loading…</div>;
  const current = rows.find((c) => c.id === open);
  const count = (pred) => rows.filter(pred).length;
  const reviewsDue = count((c) => c.probation?.review_due || c.probation?.decision_due);

  return (
    <div className="px-3 sm:px-6 py-5 space-y-5 text-zinc-100" data-testid="hiring-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-extrabold uppercase">Hiring</h1>
          <p className="text-sm text-zinc-500">{count((c) => BOARD.includes(c.stage))} in progress · {count((c) => c.stage === "probation")} on probation · {count((c) => c.stage === "active")} activated</p>
        </div>
        <button type="button" className={btn} onClick={() => copy(meta.apply_link)}><Copy className="inline h-3 w-3 mr-1" />Application link</button>
      </div>
      <div className="flex gap-1.5 flex-wrap">
        {TABS.map(([k, label]) => (
          <button key={k} type="button" onClick={() => setQ("tab", k === "pipeline" ? "" : k)} data-testid={`hiring-tab-${k}`} className={`${pill(tab === k)} !px-4 !py-1.5 !text-sm`}>
            {label}{k === "probation" && reviewsDue > 0 ? <span className="ml-1.5 rounded-full bg-amber-500 px-1.5 text-[10px] text-black">{reviewsDue}</span> : null}
            {k === "pool" ? <span className="ml-1.5 text-zinc-500">{count((c) => c.stage === "pool")}</span> : null}
          </button>
        ))}
      </div>
      {tab === "pipeline" && <Pipeline meta={meta} rows={rows} onOpen={(id) => setQ("c", id)} gymName={gymName} />}
      {tab === "interviews" && <Interviews rows={rows} onOpen={(id) => setQ("c", id)} gymName={gymName} />}
      {tab === "probation" && <ProbationTab rows={rows} onOpen={(id) => setQ("c", id)} gymName={gymName} />}
      {tab === "pool" && <Pool rows={rows} onOpen={(id) => setQ("c", id)} gymName={gymName} />}
      {tab === "slots" && <Slots meta={meta} reload={load} />}
      {tab === "setup" && <Setup meta={meta} reload={load} />}

      <Sheet open={!!open} onOpenChange={(o) => !o && setQ("c", "")}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto bg-[#0a0a0a] border-[#1f1f1f] text-zinc-100">
          <SheetHeader className="flex-row items-center justify-between space-y-0">
            <SheetTitle className="text-zinc-100">{current?.name || "Candidate"}</SheetTitle>
            <button type="button" aria-label="Close" onClick={() => setQ("c", "")} className="text-zinc-500 hover:text-white sm:hidden"><X className="h-4 w-4" /></button>
          </SheetHeader>
          {open && <div className="mt-4"><Detail id={open} meta={meta} gymName={gymName} onClose={() => setQ("c", "")} reload={load} /></div>}
        </SheetContent>
      </Sheet>
    </div>
  );
}
